import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject, catchError, map, mergeMap, of, shareReplay, switchMap } from 'rxjs';
import { Audience, AudienceReachType } from '../../audience/components/audience.model';
import { SiteContextService } from '../../core/site-context.service';
import { WidgetService } from '../../shared/services/widget.service';
import { categoriseAudience } from './audience-guesstimate.utils';
import {
  DataPlatformAudienceReachService,
  DpReachResult,
  reachBaseFilter,
  reachPeriodLabel,
  unavailableReach,
} from './data-platform-audience-reach.service';
import { CampaignWidgetResolver, campaignWidgetResolver } from './reach-estimate-factors.utils';

export interface PickerReachView {
  state: 'loading' | 'na' | 'value';
  prefix: string;
  value: number | null;
  qualifier: { key: string; params: Record<string, string | number> } | null;
  tooltipKey: string | null;
}

interface ReachJob {
  key: string;
  audience: Audience;
  siteId: number;
  featurePage: string;
}

const REACH_CONCURRENCY = 4;
const CACHE_TTL_MS = 5 * 60 * 1000;
const EVERYONE_KEY = 'everyone';
const DISPLAY_ONLY_KEYS = new Set(['filterTranslations', 'filterConfig', 'conditionTranslations', 'conditionConfig']);

/** Reach for the campaign pages' audience pickers, shared by all of them. */
@Injectable({ providedIn: 'root' })
export class AudiencePickerReachService {
  private readonly reachService = inject(DataPlatformAudienceReachService);
  private readonly siteContextService = inject(SiteContextService);
  private readonly widgetService = inject(WidgetService);

  private readonly results = signal<ReadonlyMap<string, DpReachResult>>(new Map());
  private readonly requestedAt = new Map<string, number>();
  private readonly resolvers = new Map<
    number,
    { at: number; resolver$: Observable<CampaignWidgetResolver | undefined> }
  >();
  private readonly jobs = new Subject<ReachJob>();

  constructor() {
    this.jobs
      .pipe(
        mergeMap((job) => this.run(job), REACH_CONCURRENCY),
        takeUntilDestroyed(inject(DestroyRef)),
      )
      .subscribe(({ key, result }) => this.results.update((previous) => new Map(previous).set(key, result)));
  }

  /** Undefined until resolved. Reads a signal, so a computed() calling it updates. */
  reachOf(audience: Audience): DpReachResult | undefined {
    const key = this.keyOf(audience, this.siteContextService.getCurrentSiteSync()?.id);
    return key ? this.results().get(key) : undefined;
  }

  request(audiences: readonly Audience[], featurePage: string): void {
    const siteId = this.siteContextService.getCurrentSiteSync()?.id;
    if (siteId == null) {
      return;
    }
    const now = Date.now();
    for (const audience of audiences) {
      const key = this.keyOf(audience, siteId);
      if (!key || now - (this.requestedAt.get(key) ?? -Infinity) < CACHE_TTL_MS) {
        continue;
      }
      this.requestedAt.set(key, now);
      this.jobs.next({ key, audience, siteId, featurePage });
    }
  }

  /** The audience with the picker's exact reach, so the editor opens on the same number. */
  forEditor(audience: Audience): Audience {
    const reach = this.reachOf(audience);
    return {
      ...audience,
      reach: reach && !reach.isEstimate ? (reach.value ?? -1) : undefined,
      reachType: AudienceReachType.Total,
    };
  }

  private run(job: ReachJob): Observable<{ key: string; result: DpReachResult }> {
    const { audience, siteId, featurePage } = job;
    // Campaign pages have no period, so this is the editor's fixed window there too.
    const baseFilter = reachBaseFilter(undefined, siteId);
    const result$ = audience.id
      ? this.resolverFor(audience, siteId).pipe(
          switchMap((resolver) => this.reachService.resolveReach(audience, baseFilter, resolver, featurePage)),
        )
      : this.reachService.countEveryone(siteId, featurePage);
    return result$.pipe(
      catchError(() =>
        of(unavailableReach(categoriseAudience(audience), (audience.sessionConditions?.length ?? 0) > 0)),
      ),
      map((result) => ({ key: job.key, result })),
    );
  }

  /** Campaign interaction conditions need the widgets to count exactly. */
  private resolverFor(audience: Audience, siteId: number): Observable<CampaignWidgetResolver | undefined> {
    if (!audience.sessionConditions?.length) {
      return of(undefined);
    }
    const cached = this.resolvers.get(siteId);
    if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
      return cached.resolver$;
    }
    const resolver$ = this.widgetService.getAllWidgetsSimpleInfo().pipe(
      map((widgets) => campaignWidgetResolver(widgets ?? [])),
      catchError(() => of(undefined)),
      shareReplay(1),
    );
    this.resolvers.set(siteId, { at: Date.now(), resolver$ });
    return resolver$;
  }

  private keyOf(audience: Audience, siteId: number | undefined): string | null {
    if (siteId == null) {
      return null;
    }
    const criteria = JSON.stringify(
      [audience.isDefault ? audience.name : null, audience.operator, audience.filters, audience.sessionConditions],
      (key, value) => (DISPLAY_ONLY_KEYS.has(key) ? undefined : value),
    );
    return `${siteId}|${audience.id ?? EVERYONE_KEY}|${criteria}`;
  }
}

export function pickerReachView(result: DpReachResult | undefined): PickerReachView {
  const view: PickerReachView = { state: 'loading', prefix: '', value: null, qualifier: null, tooltipKey: null };
  if (!result) {
    return view;
  }
  if (result.kind === 'na' || result.value == null) {
    return {
      ...view,
      state: 'na',
      tooltipKey: result.hasConditions ? 'data-platform.reach-conditions-note' : 'audience.cant-calculate-reach',
    };
  }
  if (result.kind === 'upper-bound') {
    let tooltipKey: string | null = null;
    if (result.hasConditions) {
      tooltipKey = 'data-platform.reach-conditions-note';
    } else if (result.unquantifiable?.includes('thirdParty')) {
      tooltipKey = 'data-platform.reach-thirdparty-bound-note';
    }
    return {
      ...view,
      state: 'value',
      prefix: '≤ ',
      value: result.value,
      qualifier: reachPeriodLabel(result.basePeriod),
      tooltipKey,
    };
  }
  if (result.kind === 'lower-bound') {
    return {
      ...view,
      state: 'value',
      prefix: '≥ ',
      value: result.value,
      qualifier: reachPeriodLabel(result.basePeriod),
      tooltipKey: 'data-platform.reach-lower-bound-note',
    };
  }
  let qualifier = reachPeriodLabel(result.basePeriod);
  let tooltipKey: string | null = null;
  if (result.factorNotes?.length) {
    tooltipKey = 'data-platform.reach-factor-approx-note';
  } else if (result.isEstimate) {
    tooltipKey = 'data-platform.flyout-estimate-body';
  } else if (!qualifier) {
    qualifier = { key: 'data-platform.reach-all-matching', params: {} };
    tooltipKey = 'data-platform.reach-all-matching-hint';
  }
  return { ...view, state: 'value', prefix: result.isEstimate ? '~' : '', value: result.value, qualifier, tooltipKey };
}
